import { z } from "zod";
import { brDateToIso, isValidCpf, onlyDigits } from "@/lib/format/br";
import type { UpdateMemberInput } from "@/lib/team/api";

type T = (key: string) => string;

const optionalDate = (t: T) =>
  z.string().trim().refine((v) => v === "" || brDateToIso(v) !== null, { message: t("validation.dateInvalid") });

export const memberDataSchema = (t: T) =>
  z
    .object({
      full_name: z.string().trim().min(1, t("validation.fullNameRequired")),
      cpf: z.string().refine((v) => onlyDigits(v) === "" || isValidCpf(v), { message: t("validation.cpfInvalid") }),
      birth_date: optionalDate(t),
      phone: z.string().refine((v) => [0, 10, 11].includes(onlyDigits(v).length), { message: t("validation.phoneInvalid") }),
      job_title: z.string().trim().max(80, t("validation.jobTitleTooLong")),
      employment_type: z.enum(["", "clt", "pj", "autonomo", "estagio"]),
      hired_at: optionalDate(t),
      terminated_at: optionalDate(t),
      is_veterinarian: z.boolean(),
      is_groomer: z.boolean(),
    })
    .superRefine((v, ctx) => {
      const hired = brDateToIso(v.hired_at);
      const terminated = brDateToIso(v.terminated_at);
      if (hired && terminated && terminated < hired) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["terminated_at"], message: t("validation.terminatedBeforeHired") });
      }
    });

export type MemberDataForm = z.infer<ReturnType<typeof memberDataSchema>>;

export function toUpdateMemberInput(userId: string, form: MemberDataForm): UpdateMemberInput {
  return {
    userId,
    fullName: form.full_name.trim(),
    cpf: onlyDigits(form.cpf) || null,
    phone: onlyDigits(form.phone) || null,
    birthDate: brDateToIso(form.birth_date),
    jobTitle: form.job_title.trim() || null,
    employmentType: form.employment_type || null,
    hiredAt: brDateToIso(form.hired_at),
    terminatedAt: brDateToIso(form.terminated_at),
    isVeterinarian: form.is_veterinarian,
    isGroomer: form.is_groomer,
  };
}

export const salarySchema = (t: T) =>
  z.object({
    base_salary: z.number().min(0, t("validation.salaryNegative")).nullable(),
    salary_period: z.enum(["monthly", "biweekly", "weekly"]),
    salary_payday: z.number().int().min(1, t("validation.paydayRange")).max(31, t("validation.paydayRange")).nullable(),
  });

export type SalaryForm = z.infer<ReturnType<typeof salarySchema>>;
